import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Chip,
  IconButton,
  Button,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Fade,
  Link as MuiLink,
  Pagination,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNotifications } from '../../Components/NotificationBell/NotificationContext';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LinkIcon from '@mui/icons-material/Link';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';


const NotificationsPage: React.FC = () => {
  const {
    notifications,
    totalNotifications,
    totalUnreadNotifications,
    filteredTotal,
    readTypeCounts,
    typeCounts,
    markAllAsRead,
    markAsRead,
    isLoading,
    refreshNotifications
  } = useNotifications();
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedNotification, setExpandedNotification] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const theme = useTheme();
  const isAdmin = localStorage.getItem('role') === 'admin';
  const navigate = useNavigate();



  // Funkcja do konwersji filtrów na parametry API
  const getReadFilterValue = (filter: 'all' | 'unread' | 'read'): boolean | null => {
    if (filter === 'all') return null;
    return filter === 'read';
  };

  // Dodajemy efekt, który odświeży powiadomienia przy montowaniu komponentu
  // oraz gdy zmienią się filtry
  useEffect(() => {
    console.log('Refreshing notifications with filters:', {
      readFilter: filter,
      typeFilter: typeFilter,
      page: page
    });

    // Create a unique key for this particular set of filters to help with debugging
    const currentFilterKey = `${filter}-${typeFilter}-${page}`;
    console.log('Filter key:', currentFilterKey);

    // Check if the current page is valid for the filtered results
    // If we're not on page 1 and we have type counts, ensure we're not beyond available pages
    if (page > 1) {
      // If type filter is active, check against that type's count
      if (typeFilter !== 'all' && typeCounts && Object.keys(typeCounts).length > 0) {
        const typeCount = typeCounts[typeFilter as keyof typeof typeCounts];
        const maxPage = Math.max(1, Math.ceil(typeCount / itemsPerPage));
        if (page > maxPage) {
          setPage(1);
          return; // Early return to prevent the API call, it will happen after the state update
        }
      }
      // Otherwise check against filtered total
      else if (filteredTotal > 0) {
        const maxPage = Math.max(1, Math.ceil(filteredTotal / itemsPerPage));
        if (page > maxPage) {
          setPage(1);
          return; // Early return to prevent the API call, it will happen after the state update
        }
      }
    }

    const skip = (page - 1) * itemsPerPage;
    const isRead = getReadFilterValue(filter);
    const type = typeFilter === 'all' ? null : typeFilter;

    // Use setTimeout to ensure this executes after the current render cycle
    // This helps prevent race conditions where multiple filter changes happen quickly
    const timeoutId = setTimeout(() => {
      refreshNotifications(skip, itemsPerPage, isRead, type);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [refreshNotifications, page, itemsPerPage, filter, typeFilter]);

  // Reset to page 1 when the type filter changes
  useEffect(() => {
    if (page > 1 && typeFilter !== 'all') {
      const typeCount = typeCounts[typeFilter as keyof typeof typeCounts];
      const maxPage = Math.max(1, Math.ceil(typeCount / itemsPerPage));
      if (page > maxPage) {
        console.log(`Page ${page} exceeds max pages (${maxPage}) for type ${typeFilter}, resetting to page 1`);
        setPage(1);
      }
    }
  }, [typeFilter, typeCounts, page, itemsPerPage]);

  // Dodaj console.log do debugowania
  console.log('Notifications in page:', notifications);

  /** Formatowanie upływu czasu */
  const formatRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Przed chwilą';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} min temu`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} godz. temu`;
    } else if (diffInMinutes < 10080) { // 7 dni
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} dni temu`;
    } else {
      return format(date, 'd MMMM yyyy, HH:mm', { locale: pl });
    }
  };

  /** Zamiana linków w treści na odnośniki */
  const linkify = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <MuiLink
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'primary.main',
              '&:hover': {
                color: 'primary.dark',
              },
            }}
          >
            <LinkIcon sx={{ fontSize: '0.9em' }} />
            {part}
          </MuiLink>
        );
      }
      return part;
    });
  };

  /** Przypisanie kolorów do typów */
  const getChipColor = (
    type: string
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  /** Czytelna etykieta typu */
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'error':
        return 'Błąd';
      case 'warning':
        return 'Ostrzeżenie';
      case 'success':
        return 'Sukces';
      case 'info':
        return 'Informacja';
      default:
        return type;
    }
  };

  /** Statystyki powiadomień (łącznie, nieprzeczytane, przeczytane, wg typu) */
  const notificationStats = useMemo(() => {
    // Calculate read count by subtracting unread from total
    const readCount = totalNotifications - totalUnreadNotifications;

    return {
      total: totalNotifications,
      unread: totalUnreadNotifications,
      read: readCount,
      filtered: filteredTotal,
      byType: typeCounts,
    };
  }, [totalNotifications, totalUnreadNotifications, typeCounts, filteredTotal]);

  // Get the current active count based on filters
  const activeCount = useMemo(() => {
    // If both read filter and type filter are active, use the combined count
    if (filter !== 'all' && typeFilter !== 'all') {
      const readStatus = filter === 'read' ? 'read' : 'unread';
      const countKey = `${readStatus}_${typeFilter}`;
      return readTypeCounts[countKey] || 0;
    }

    // If only type filter is active
    if (typeFilter !== 'all') {
      return typeCounts[typeFilter as keyof typeof typeCounts] || 0;
    }

    // If only read filter is active
    if (filter !== 'all') {
      return filter === 'read' ?
        (totalNotifications - totalUnreadNotifications) :
        totalUnreadNotifications;
    }

    // Otherwise use filtered total
    return filteredTotal;
  }, [filteredTotal, filter, typeFilter, typeCounts, readTypeCounts, totalNotifications, totalUnreadNotifications]);

  /** Oblicz całkowitą liczbę stron na podstawie przefiltrowanej liczby powiadomień */
  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(activeCount / itemsPerPage));
  }, [activeCount, itemsPerPage]);

  // Verify that displayed notifications match current filters
  // This is a safety check in case there's a bug in the API or context
  const verifiedNotifications = useMemo(() => {
    // For UI display, we'll do a final verification to ensure what's shown
    // matches the active filters
    if (filter !== 'all') {
      const shouldBeRead = filter === 'read';
      const notificationsWithWrongReadStatus = notifications.filter(n => n.read !== shouldBeRead);

      if (notificationsWithWrongReadStatus.length > 0) {
        console.error('Found notifications with wrong read status:', notificationsWithWrongReadStatus);
        // Return only notifications with correct read status
        return notifications.filter(n => n.read === shouldBeRead);
      }
    }

    if (typeFilter !== 'all') {
      const notificationsWithWrongType = notifications.filter(n => n.type !== typeFilter);

      if (notificationsWithWrongType.length > 0) {
        console.error('Found notifications with wrong type:', notificationsWithWrongType);
        // Return only notifications with correct type
        return notifications.filter(n => n.type === typeFilter);
      }
    }

    // If everything is correct, return all notifications
    return notifications;
  }, [notifications, filter, typeFilter]);

  // Dodaj logi debugowe
  console.log('Pagination debug:', {
    notificationsLength: notifications.length,
    verifiedNotificationsLength: verifiedNotifications.length,
    totalNotifications,
    filteredTotal,
    activeCount,
    readTypeCounts,
    itemsPerPage,
    totalPages,
    currentPage: page,
    filterState: filter,
    typeFilterState: typeFilter,
    typeCounts
  });

  /** Handler zmiany strony */
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    // Make sure we don't try to access a page beyond what's available
    const newPage = Math.min(value, totalPages);

    if (newPage !== page) {
      setPage(newPage);

      // Przewiń do góry listy przy zmianie strony
      const listContainer = document.querySelector('.notifications-list-container');
      if (listContainer) {
        listContainer.scrollTop = 0;
      }
    }
  };

  // Modyfikujemy handlry zmiany filtrów, aby resetowały stronę
  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: 'all' | 'unread' | 'read') => {
    if (newFilter && newFilter !== filter) {
      // Set page first to ensure useEffect behaves properly
      setPage(1);
      // Set filter after page to trigger useEffect only once
      setFilter(newFilter);
    }
  };

  const handleTypeFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: string) => {
    if (newFilter && newFilter !== typeFilter) {
      // Set page first to ensure useEffect behaves properly
      setPage(1);
      // Set filter after page to trigger useEffect only once
      setTypeFilter(newFilter);
    }
  };

  // Add debug console log to verify current filter state
  useEffect(() => {
    console.log('Current filter state changed:', {
      readFilter: filter,
      typeFilter: typeFilter,
      page: page
    });
  }, [filter, typeFilter, page]);

  /**
   * Render pojedynczego elementu listy
   * Przyjmuje `notification` i informację, czy to powiadomienie jest rozwinięte
   */
  const renderNotificationItem = (notification: any, isExpanded: boolean) => {
    if (isExpanded) {
      // widok ROZWINIĘTY
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            height: '100%',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {/* Pasek nagłówka */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Tooltip
              title="Zamknij"
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedNotification(null);
                }}
                size="small"
                sx={{
                  bgcolor: theme.name === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.03)',
                  '&:hover': {
                    bgcolor: theme.name === 'dark'
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                flex: 1,
                fontWeight: 500,
                color: theme.name === 'dark'
                  ? 'primary.light'
                  : 'primary.dark',
              }}
            >
              {notification.title}
            </Typography>
          </Box>

          {/* Pasek z tagami (typ, czas itp.) */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Chip
              label={getTypeLabel(notification.type)}
              color={getChipColor(notification.type)}
              sx={{
                fontWeight: 500,
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: theme.name === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.03)',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
              }}
            >
              <AccessTimeIcon fontSize="small" />
              {formatRelativeTime(notification.timestamp)}
            </Typography>
          </Box>

          {/* Treść powiadomienia */}
          <Paper
            elevation={theme.name === 'dark' ? 2 : 1}
            sx={{
              p: 2,
              flex: 1,
              overflow: 'auto',
              maxHeight: '100%',
              height: '100%',
              bgcolor: theme.name === 'dark'
                ? 'rgba(255, 255, 255, 0.03)'
                : 'rgba(0, 0, 0, 0.02)',
              border: '1px solid',
              borderColor: theme.name === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'divider',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                color: theme.name === 'dark'
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'inherit',
                lineHeight: 1.7,
                flex: 1,
              }}
            >
              {linkify(notification.message)}
            </Typography>
          </Paper>
        </Box>
      );
    } else {
      // widok ZWINIĘTY
      return (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              width: '100%',
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                {notification.title}
              </Typography>
              <Chip
                label={getTypeLabel(notification.type)}
                size="small"
                color={getChipColor(notification.type)}
                sx={{
                  fontWeight: 500,
                  minWidth: 90,
                }}
              />
              {!notification.read && (
                <Chip
                  label="Nowe"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 'fit-content',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontStyle: 'italic',
                  minWidth: 100,
                  textAlign: 'right',
                }}
              >
                {formatRelativeTime(notification.timestamp)}
              </Typography>
              {!notification.read && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    markAsRead(notification.id);
                  }}
                  size="small"
                  sx={{
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
          <Typography
            variant="body1"
            color="text.primary"
            sx={{
              borderLeft: 3,
              borderColor: `${getChipColor(notification.type)}.main`,
              ml: 1,
              pl: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              lineHeight: '1.5em',
            }}
          >
            {notification.message}
          </Typography>
        </>
      );
    }
  };

  return (
    <Box
      sx={{
        // Główny kontener wypełniający okno
        display: 'flex',
        flexDirection: 'column',
        height: '100vh', // Możesz zmienić na np. "calc(100vh - 64px)" jeśli masz AppBar itp.
        bgcolor: theme.name === 'dark' ? 'background.default' : 'inherit',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          // Paper również na 100% wysokości (rozdzielenie w pionie)
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // Usuwamy 'overflow: hidden' TYLKO jeśli chcesz mieć w Paper osobny scrollbar
          bgcolor: theme.name === 'dark' ? 'background.paper' : 'inherit',
          borderColor: theme.name === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'divider',
        }}
      >
        {/* Sekcja górna: nagłówek, przyciski, filtry */}
        <Box sx={{ p: 2, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 500,
                  mb: 1,
                  color: theme.name === 'dark'
                    ? 'primary.light'
                    : 'primary.dark',
                }}
              >
                Powiadomienia
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Łącznie: {notificationStats.total} | Nieprzeczytane:{' '}
                {notificationStats.unread} | Przeczytane:{' '}
                {notificationStats.read}
                {filteredTotal !== totalNotifications && (
                  <> | <strong>Wyfiltrowano: {filteredTotal}</strong></>
                )}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              {isAdmin && (
                <Button
                  variant="outlined"
                  onClick={() => navigate('/notifications/add')}
                  sx={{
                    borderColor: theme.name === 'dark' ? 'primary.light' : 'primary.main',
                    color: theme.name === 'dark' ? 'primary.light' : 'primary.main',
                    '&:hover': {
                      borderColor: theme.name === 'dark' ? 'primary.main' : 'primary.dark',
                      bgcolor: theme.name === 'dark'
                        ? 'rgba(144, 202, 249, 0.08)'
                        : 'rgba(25, 118, 210, 0.04)',
                    }
                  }}
                >
                  Nowe powiadomienie
                </Button>
              )}
              <Button
                startIcon={<DoneAllIcon />}
                onClick={markAllAsRead}
                variant="outlined"
                color="primary"
                size="small"
                sx={{
                  borderColor: theme.name === 'dark' ? 'primary.light' : 'primary.main',
                  color: theme.name === 'dark' ? 'primary.light' : 'primary.main',
                  '&:hover': {
                    borderColor: theme.name === 'dark' ? 'primary.main' : 'primary.dark',
                    bgcolor: theme.name === 'dark'
                      ? 'rgba(144, 202, 249, 0.08)'
                      : 'rgba(25, 118, 210, 0.04)',
                  }
                }}
              >
                Oznacz wszystkie jako przeczytane
              </Button>
            </Stack>
          </Box>

          {/* Filtry w ToggleButtonGroup */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
            sx={{

              width: '100%',
              overflowX: 'auto',
              pb: 1,
              '& .MuiToggleButtonGroup-root': {
                bgcolor: theme.name === 'dark'
                  ? 'background.secondary'
                  : 'background.paper',
                boxShadow: theme.name === 'dark'
                  ? '0 2px 8px rgba(0,0,0,0.2)'
                  : '0 1px 4px rgba(0,0,0,0.1)',
              }
            }}
          >
            {/* Filtr: wszystkie / nieprzeczytane / przeczytane */}
            <ToggleButtonGroup
              value={filter}
              exclusive
              onChange={handleFilterChange}
              size="small"
            >
              <ToggleButton value="all">
                Wszystkie ({notificationStats.total})
              </ToggleButton>
              <ToggleButton
                value="unread"
                disabled={notificationStats.unread === 0}
                sx={{
                  '&.Mui-selected': {
                    color: 'error.contrastText',
                    borderColor: 'error.main',
                    bgcolor: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.dark',
                    },
                  },
                }}
              >
                Nieprzeczytane ({notificationStats.unread})
              </ToggleButton>
              <ToggleButton
                value="read"
                disabled={notificationStats.read === 0}
                sx={{
                  '&.Mui-selected': {
                    color: 'success.contrastText',
                    borderColor: 'success.main',
                    bgcolor: 'success.main',
                    '&:hover': {
                      bgcolor: 'success.dark',
                    },
                  },
                }}
              >
                Przeczytane ({notificationStats.read})
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Filtr: typ powiadomienia */}
            <ToggleButtonGroup
              value={typeFilter}
              exclusive
              onChange={handleTypeFilterChange}
              size="small"
            >
              <ToggleButton value="all">
                Wszystkie typy ({notificationStats.total})
              </ToggleButton>
              <ToggleButton
                value="error"
                sx={{
                  borderColor: 'error.main !important',
                  '&.Mui-selected': {
                    color: 'error.contrastText',
                    borderColor: 'error.main',
                    bgcolor: 'error.main',
                    '&:hover': {
                      bgcolor: 'error.dark',
                    },
                  },
                }}
                disabled={notificationStats.byType.error === 0}
              >
                Błędy ({notificationStats.byType.error})
              </ToggleButton>
              <ToggleButton
                value="warning"
                sx={{
                  borderColor: 'warning.main !important',
                  '&.Mui-selected': {
                    color: 'warning.contrastText',
                    borderColor: 'warning.main',
                    bgcolor: 'warning.main',
                    '&:hover': {
                      bgcolor: 'warning.dark',
                    },
                  },
                }}
                disabled={notificationStats.byType.warning === 0}
              >
                Ostrzeżenia ({notificationStats.byType.warning})
              </ToggleButton>
              <ToggleButton
                value="success"
                sx={{
                  borderColor: 'success.main !important',
                  '&.Mui-selected': {
                    color: 'success.contrastText',
                    borderColor: 'success.main',
                    bgcolor: 'success.main',
                    '&:hover': {
                      bgcolor: 'success.dark',
                    },
                  },
                }}
                disabled={notificationStats.byType.success === 0}
              >
                Sukcesy ({notificationStats.byType.success})
              </ToggleButton>
              <ToggleButton
                value="info"
                sx={{
                  borderColor: 'info.main !important',
                  '&.Mui-selected': {
                    color: 'info.contrastText',
                    borderColor: 'info.main',
                    bgcolor: 'info.main',
                    '&:hover': {
                      bgcolor: 'info.dark',
                    },
                  },
                }}
                disabled={notificationStats.byType.info === 0}
              >
                Info ({notificationStats.byType.info})
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Box>

        {/* LISTA z powiadomieniami + obsługa przewijania */}
        <Box
          className="notifications-list-container"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            pt: 0,
            px: 2,
            pb: 2,
            height: 'calc(100vh - 250px)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                right: 10,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                backgroundColor: theme.name === 'dark'
                  ? 'rgba(0, 0, 0, 0.7)'
                  : 'rgba(255, 255, 255, 0.8)',
                padding: '4px 10px',
                borderRadius: '16px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              <CircularProgress size={20} color="primary" />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Odświeżanie...
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              height: '100%',
              bgcolor: theme.name === 'dark'
                ? 'rgba(0, 0, 0, 0.2)'
                : 'rgba(0, 0, 0, 0.2)',
              borderRadius: 2,
              p: 3,
              border: '1px solid',
              borderColor: theme.name === 'dark'
                ? 'rgba(255, 255, 255, 0.05)'
                : 'divider',
              boxShadow: theme.name === 'dark'
                ? '0 4px 12px rgba(0,0,0,0.3)'
                : '0 2px 8px rgba(0,0,0,0.05)',
              overflow: 'auto',
            }}
          >
            {expandedNotification ? (
              <Box
                sx={{
                  height: '100%',
                  bgcolor: theme.name === 'dark'
                    ? 'background.secondary'
                    : 'background.paper',
                  p: 2,
                  borderRadius: 1,
                  position: 'relative',
                  border: '1px solid',
                  borderColor: theme.name === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'divider',
                  boxShadow: theme.name === 'dark'
                    ? '0 2px 8px rgba(0,0,0,0.2)'
                    : '0 1px 4px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {(() => {
                  const notification = verifiedNotifications.find(
                    (n) => n.id === expandedNotification
                  );
                  if (!notification) return null;

                  return renderNotificationItem(notification, true);
                })()}
              </Box>
            ) : (
              <>
                {isLoading && notifications.length === 0 ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress color="primary" />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                      Ładowanie powiadomień...
                    </Typography>
                  </Box>
                ) : (
                  <List
                    sx={{
                      width: '100%',
                      '& .MuiListItem-root': {
                        mb: 2,
                        border: '1px solid',
                        borderColor: theme.name === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'divider',
                        borderRadius: 1,
                        overflow: 'hidden',
                        boxShadow: theme.name === 'dark'
                          ? '0 2px 8px rgba(0,0,0,0.2)'
                          : '0 1px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        bgcolor: theme.name === 'dark'
                          ? 'background.secondary'
                          : 'background.paper',
                        '&:hover': {
                          borderColor: theme.name === 'dark'
                            ? 'primary.dark'
                            : 'primary.light',
                          transform: 'translateY(-1px)',
                          boxShadow: theme.name === 'dark'
                            ? '0 4px 12px rgba(0,0,0,0.3)'
                            : '0 2px 8px rgba(0,0,0,0.15)',
                        }
                      },
                    }}
                  >
                    {verifiedNotifications.length === 0 ? (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                          Brak powiadomień spełniających kryteria filtrowania
                        </Typography>
                      </Box>
                    ) : (
                      // Use verified notifications to ensure display matches filters
                      verifiedNotifications.map((notification) => (
                        <ListItem
                          key={notification.id}
                          sx={{
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            bgcolor: notification.read
                              ? 'background.notification.read'
                              : 'background.notification.unread',
                            borderColor: theme.name === 'dark'
                              ? `${getChipColor(notification.type)}.dark`
                              : `${getChipColor(notification.type)}.main`,
                            p: 2,
                            '&:hover': {
                              cursor: 'pointer',
                              borderColor: `${getChipColor(notification.type)}.main`,
                            },
                          }}
                          onClick={() => {
                            if (expandedNotification !== notification.id) {
                              setExpandedNotification(notification.id);
                              if (!notification.read) {
                                markAsRead(notification.id);
                              }
                            }
                          }}
                        >
                          {renderNotificationItem(notification, expandedNotification === notification.id)}
                        </ListItem>
                      ))
                    )}
                  </List>
                )}
              </>
            )}
          </Box>

          {/* Dodaj paginację na dole */}
          {!expandedNotification && activeCount > itemsPerPage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
                pb: 1,
                borderTop: '1px solid',
                borderColor: theme.name === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'divider',
                bgcolor: theme.name === 'dark'
                  ? 'background.secondary'
                  : 'background.paper',
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  marginLeft: '200px',
                  '& .MuiPaginationItem-root': {
                    color: theme.name === 'dark' ? 'primary.light' : 'primary.main',
                    '&.Mui-selected': {
                      bgcolor: theme.name === 'dark'
                        ? 'rgba(144, 202, 249, 0.16)'
                        : 'primary.lighter',
                      '&:hover': {
                        bgcolor: theme.name === 'dark'
                          ? 'rgba(144, 202, 249, 0.24)'
                          : 'primary.light',
                      },
                    },
                  },
                }}
              />

              {/* Add information about current page and total pages */}
              <Typography
                variant="caption"
                sx={{
                  ml: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: theme.name === 'dark' ? 'text.secondary' : 'text.primary'
                }}
              >
                Strona {page} z {totalPages} ({activeCount} powiadomień{typeFilter !== 'all' ? ` typu ${getTypeLabel(typeFilter).toLowerCase()}` : ''})
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default NotificationsPage;
